/**
 * Was nur in der Android-App nötig ist.
 *
 * Im Browser fragt `navigator.geolocation` selbst nach Erlaubnis. In der
 * App dagegen liegt die Entscheidung beim Betriebssystem, und die WebView
 * bekommt den Standort erst, wenn die App die Berechtigung zur Laufzeit
 * erhalten hat - sonst meldet `watchPosition` schlicht nichts, ohne Fehler
 * und ohne Rückfrage.
 */
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { App } from '@capacitor/app';

/** Läuft die Oberfläche in der App statt im Browser? */
export const istApp = () => Capacitor.isNativePlatform();

/**
 * Ortung freischalten. Wird beim Start einmal aufgerufen; ist die Erlaubnis
 * schon erteilt, kostet das nichts. Eine Ablehnung ist kein Fehler - die Karte
 * funktioniert auch ohne, nur ohne eigenen Standort.
 */
export async function ortungVorbereiten() {
  if (!istApp()) return null;
  try {
    const stand = await Geolocation.checkPermissions();
    if (stand.location === 'granted' || stand.coarseLocation === 'granted') return stand;
    return await Geolocation.requestPermissions({ permissions: ['location'] });
  } catch {
    return null;
  }
}

/**
 * Die Zurück-Taste von Android.
 *
 * Ohne eigenes Zutun blättert Capacitor im Verlauf der WebView zurück und
 * beendet die App, sobald es nichts mehr gibt. Die ganze Karte ist aber eine
 * einzige Seite - es gab nie etwas, und ein Druck führte aus der Route direkt
 * auf den Startbildschirm. `behandeln` bekommt die Entscheidung und meldet
 * zurück, ob es etwas geschlossen hat; wenn nicht, geht die App in den
 * Hintergrund, statt zu enden. Dort steht sie weiter und lässt sich aus der
 * Übersicht zurückholen.
 */
export async function zurueckTaste(behandeln) {
  if (!istApp()) return;
  await App.addListener('backButton', async () => {
    if (behandeln()) return;
    try {
      await App.minimizeApp();
    } catch {
      /* Klappt das nicht, bleibt die App eben offen - besser als zu enden. */
    }
  });
}
